import { Formik, useField } from 'formik';
import { useState } from 'react';
import {
  CompanyPageCompanyFragment,
  useCompanyPageUpdateCompanyMutation,
} from 'generated/graphql';
import {
  enableDisableRadioButtonOptions,
  EnableDisableValue,
} from 'views/Company/CompanyView/CompanyView.helpers';
import {
  EditFormWrapper,
  FormikRadioButtonGroup,
  InlineNotification,
  NotificationType,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import {
  AI_INTERVIEW_SCORE_HIDING_AVAILABLE_LABEL,
  AI_INTERVIEW_SCORE_HIDING_DATA_TEST_ID,
  SWITCH_OFF_NOTIFICATION_MESSAGE,
} from './AiInterviewScoreHidingSection.constants';
import styles from './AiInterviewScoreHidingSection.module.scss';

type AiInterviewScoreHidingSectionFormValues = {
  aiInterviewScoreHidingAvailable: string;
};

type AiInterviewScoreHidingSectionFormProps = {
  company: CompanyPageCompanyFragment;
  onCancelClick: () => void;
  onSave: () => void;
};

const AiInterviewScoreHidingSectionForm = ({
  company,
  onCancelClick,
  onSave,
}: AiInterviewScoreHidingSectionFormProps) => {
  const { handleMsgType } = useNotification();
  const [isSwitchChanged, setIsSwitchChanged] = useState(false);

  const [editCompanyMutation] = useCompanyPageUpdateCompanyMutation();

  const handleSave = (values: AiInterviewScoreHidingSectionFormValues) => {
    editCompanyMutation({
      variables: {
        id: company.id,
        name: company.name,
        isAiInterviewScoreHidingAvailable:
          values.aiInterviewScoreHidingAvailable === EnableDisableValue.Enabled,
      },
      onCompleted: () => {
        handleMsgType({
          type: TNotification.success,
          title: 'AI interview score settings updated.',
          dismissable: true,
        });
        onSave();
      },
      onError: () => {
        handleMsgType({
          type: TNotification.error,
          title: 'AI interview score settings could not be updated',
          message: 'Something went wrong. Please try again.',
          dismissable: true,
        });
      },
    });
  };

  return (
    <Formik<AiInterviewScoreHidingSectionFormValues>
      initialValues={{
        aiInterviewScoreHidingAvailable:
          company.isAiInterviewScoreHidingAvailable
            ? EnableDisableValue.Enabled
            : EnableDisableValue.Disabled,
      }}
      onSubmit={handleSave}
    >
      {({ submitForm, values }) => (
        <EditFormWrapper
          onCancelClick={onCancelClick}
          onSaveClick={submitForm}
          title="Hide AI interview scores"
          formId="ai-interview-score-hiding-form"
          hasResetAllButton={false}
          data-testid={AI_INTERVIEW_SCORE_HIDING_DATA_TEST_ID}
        >
          <form
            onSubmit={event => event.preventDefault()}
            id="ai-interview-score-hiding-form-element"
            className={styles.form}
          >
            <div>
              <FormikRadioButtonGroup
                id="aiInterviewScoreHidingAvailable"
                name="aiInterviewScoreHidingAvailable"
                options={enableDisableRadioButtonOptions}
                layout="horizontal"
                label={AI_INTERVIEW_SCORE_HIDING_AVAILABLE_LABEL}
                data-testid={`${AI_INTERVIEW_SCORE_HIDING_DATA_TEST_ID}__available-radio-group`}
                useFormikField={useField}
                onChange={() => setIsSwitchChanged(true)}
                className={styles.radioGroup}
              />
              {isSwitchChanged &&
                values.aiInterviewScoreHidingAvailable ===
                  EnableDisableValue.Disabled && (
                  <InlineNotification
                    notificationType={NotificationType.NEUTRAL}
                    className={styles.inlineNotification}
                  >
                    {SWITCH_OFF_NOTIFICATION_MESSAGE}
                  </InlineNotification>
                )}
            </div>
          </form>
        </EditFormWrapper>
      )}
    </Formik>
  );
};

export default AiInterviewScoreHidingSectionForm;
